import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Box,
  IconButton,
  Paper,
  Slider,
  Typography,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import HomeIcon from '@mui/icons-material/Home';
import { sendPTZCommand } from 'api/canvms';

const HOLD_INTERVAL = 100; // ms between commands when holding

function PTZControls({ siteId, cameraId, disabled = false, onCommand }) {
  const [speed, setSpeed] = useState(0.5);
  const [activeCommand, setActiveCommand] = useState(null);
  const [loading, setLoading] = useState(false);
  const holdIntervalRef = useRef(null);
  const isHoldingRef = useRef(false);

  // Send command to backend
  const executeCommand = useCallback(async (command) => {
    if (disabled || !siteId || !cameraId) return;

    try {
      setLoading(true);
      await sendPTZCommand(siteId, cameraId, command, speed);
      onCommand?.(command);
    } catch (err) {
      console.error('[CANVMS/PTZControls] Error sending command:', err);
    } finally {
      setLoading(false);
    }
  }, [siteId, cameraId, speed, disabled, onCommand]);

  // Send stop command
  const sendStop = useCallback(async () => {
    if (disabled || !siteId || !cameraId) return;

    try {
      await sendPTZCommand(siteId, cameraId, 'stop');
    } catch (err) {
      console.error('[CANVMS/PTZControls] Error sending stop:', err);
    }
  }, [siteId, cameraId, disabled]);

  // Handle button press (start continuous movement)
  const handleMouseDown = useCallback((command) => {
    if (disabled) return;

    setActiveCommand(command);
    isHoldingRef.current = true;
    executeCommand(command);

    // Start interval for continuous movement
    holdIntervalRef.current = setInterval(() => {
      if (isHoldingRef.current) {
        executeCommand(command);
      }
    }, HOLD_INTERVAL);
  }, [disabled, executeCommand]);

  // Handle button release (stop movement)
  const handleMouseUp = useCallback(() => {
    isHoldingRef.current = false;
    setActiveCommand(null);

    if (holdIntervalRef.current) {
      clearInterval(holdIntervalRef.current);
      holdIntervalRef.current = null;
    }

    // Send stop command
    sendStop();
  }, [sendStop]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (holdIntervalRef.current) {
        clearInterval(holdIntervalRef.current);
      }
    };
  }, []);

  // Keyboard support
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (disabled) return;

      // Only handle if this component is focused or no input is focused
      if (document.activeElement.tagName === 'INPUT' || document.activeElement.tagName === 'TEXTAREA') {
        return;
      }

      let command = null;
      switch (e.key) {
        case 'ArrowUp':
          command = 'up';
          break;
        case 'ArrowDown':
          command = 'down';
          break;
        case 'ArrowLeft':
          command = 'left';
          break;
        case 'ArrowRight':
          command = 'right';
          break;
        case '+':
        case '=':
          command = 'zoom_in';
          break;
        case '-':
          command = 'zoom_out';
          break;
        case 'Home':
          command = 'center';
          break;
        default:
          return;
      }

      e.preventDefault();
      if (!isHoldingRef.current) {
        handleMouseDown(command);
      }
    };

    const handleKeyUp = (e) => {
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', '+', '=', '-', 'Home'].includes(e.key)) {
        handleMouseUp();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [disabled, handleMouseDown, handleMouseUp]);

  const buttonSx = {
    width: 48,
    height: 48,
    bgcolor: 'grey.200',
    '&:hover': { bgcolor: 'grey.300' },
    '&:active': { bgcolor: 'primary.light' },
    '&.Mui-disabled': { bgcolor: 'grey.100' },
  };

  const activeButtonSx = {
    ...buttonSx,
    bgcolor: 'primary.light',
  };

  return (
    <Paper
      sx={{
        p: 2,
        opacity: 0.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="subtitle2">PTZ Controls</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
        Pending VMS migration
      </Typography>
    </Paper>
  );
}

export default PTZControls;
